import { useEffect, useState } from 'react'
import type { RefObject } from 'react'
import type maplibregl from 'maplibre-gl'
import { cabinetApi } from '../api'
import type { BBox } from '../api'
import { isobathZoomStep } from './isobaths'

/**
 * Минимум, который хук просит у карты. Ровно эти четыре ручки есть и у
 * `MapCanvasHandle`, и у самодельного переходника раздела «Буи».
 *
 * Интерфейс, а не тип `MapCanvasHandle`, потому что карт в кабинете НЕ одна:
 * у «Буёв» и «Спутников» свои `maplibregl.Map`, а логика «что просить и когда»
 * обязана остаться одной — иначе дебаунс, пустой ответ и обрыв сети разойдутся
 * между разделами.
 */
export interface EezMapSink {
  getMap: () => maplibregl.Map | null
  getBBox: () => BBox
  setEez: (data: GeoJSON.FeatureCollection) => void
  removeEez: () => void
}

/**
 * Оверлей «Границы ИЭЗ»: рубежи экономических зон поверх любого слоя.
 *
 * Общий хук на ВСЕ карты кабинета — «Карты», «Аналитика», «Буи». Карты у них
 * разные, а вопрос один: что просить у сервера и когда.
 *
 * Даты у слоя нет: границы меняются раз в годы. Зато есть ВЬЮПОРТ — и этим он
 * отличается от землетрясений, которые `useOverlays` не перезапрашивает на пан:
 * рубежи режутся и упрощаются по кадру, иначе один 200-мильный предел России
 * (6,5 тыс. км линии) приезжал бы целиком на каждом зуме.
 *
 * Запрос дебаунсится по той же причине, что у изобат: пан и инерция зума сыплют
 * `moveend`, а каждый ответ — десятки-сотни килобайт геометрии.
 */
const DEBOUNCE_MS = 250

export function useEezLayer(
  mapRef: RefObject<EezMapSink | null>,
  enabled: boolean,
  viewKey: string,
): { attribution: string | null; disclaimerKey: string | null } {
  // Подпись источника показывается, ТОЛЬКО когда слой реально что-то нарисовал:
  // CC-BY требует атрибуции к показанным данным, а плашка над пустой картой
  // обещала бы границы, которых в кадре нет.
  const [meta, setMeta] = useState<{ attribution: string; disclaimerKey: string } | null>(null)

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (!enabled) {
      map.removeEez()
      setMeta(null)
      return
    }
    let alive = true
    const id = setTimeout(() => {
      // Зум берём У САМОЙ КАРТЫ: от него зависит упрощение геометрии на
      // бэкенде, и запрошенный «на глазок» дал бы либо гранёные линии, либо
      // мегабайты.
      const zoom = isobathZoomStep(map.getMap()?.getZoom() ?? 6)
      cabinetApi
        .eez({ bbox: map.getBBox(), zoom })
        .then(fc => {
          if (!alive) return
          if (!fc.features?.length) {
            // Вне покрытия загрузки рубежей нет — это не сбой, но и старые
            // линии чужого бассейна висеть не должны.
            map.removeEez()
            setMeta(null)
            return
          }
          map.setEez(fc as unknown as GeoJSON.FeatureCollection)
          setMeta(
            fc.meta
              ? { attribution: fc.meta.attribution, disclaimerKey: fc.meta.disclaimer_key }
              : null,
          )
        })
        .catch(() => {
          if (!alive) return
          map.removeEez()
          setMeta(null)
        })
    }, DEBOUNCE_MS)
    return () => {
      alive = false
      clearTimeout(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, viewKey])

  // Уход из раздела → снять линии, иначе повиснут над другой картой.
  useEffect(
    () => () => mapRef.current?.removeEez(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return {
    attribution: meta?.attribution ?? null,
    disclaimerKey: meta?.disclaimerKey ?? null,
  }
}
